import { ComponentProps, ReactNode, Ref } from 'react'
import { Link, type LinkProps, Path } from 'react-router'

import { Spinner } from '../../../icons'
import { SizeOptions } from '../../../types/types'
import { AstraParagraph } from '../../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../../Utilities/astraTwMerge'

export type Variant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'magic' | 'critical' | 'critical-secondary'

type State = 'rest' | 'hover' | 'active' | 'disabled'

type CommonProps = {
    /**
     * The variant to use
     */
    variant?: Variant
    /**
     * The size of the button
     */
    size?: SizeOptions
    /**
     * The label to use
     */
    label?: string
    /**
     * Icon to display before the label or by itself
     */
    icon?: ReactNode
    /**
     * If `true`, the component displays a loading indicator
     */
    isLoading?: boolean
    /**
     * If `true`, the button will take up the full width of its container.
     */
    fullWidth?: boolean
    /**
     * An icon to display at the end of the button, after the label.
     */
    endIcon?: ReactNode
    /**
     * If `true`, the button will have less left and right padding.
     */
    tightPadding?: boolean
    /**
     * If `true`, the label will be truncated with ellipsis when there's not enough space.
     */
    truncate?: boolean
    /**
     * Is the button rounded. By default, buttons with labels and icon-only buttons use rounded corners. Setting this to `false` will make the button square.
     */
    isRounded?: boolean
}

export type AstraButtonOnlyProps = CommonProps &
    Omit<ComponentProps<'button'>, 'size'> & {
        to?: never
    }

export type AstraLinkButtonProps = CommonProps &
    Omit<LinkProps, 'size'> & {
        /**
         * Ref to the anchor element
         */
        ref?: Ref<HTMLAnchorElement>
        /**
         * Path to navigate to when clicked. Renders a Link component instead of a button.
         */
        to: string | Partial<Path>
        /**
         * If `true`, the link will appear disabled (pointer-events-none + reduced opacity)
         */
        disabled?: boolean
    }

export type AstraButtonProps = AstraButtonOnlyProps | AstraLinkButtonProps

const outlineStyles = 'focus-within:outline-1 focus-within:outline-offset-4 focus-within:outline-ring'

const baseStyles = `group max-w-full flex items-center justify-center gap-1 overflow-hidden transition-all select-none border ${outlineStyles} disabled:cursor-not-allowed text-nowrap`

const primaryStyles: Record<State, string> = {
    rest: 'bg-brand-strong-rest border-transparent',
    hover: 'hover:bg-brand-strong-hover',
    active: 'active:bg-brand-strong-active active:shadow-button-active',
    disabled: 'disabled:bg-brand-strong-disabled',
}

const secondaryStyles: Record<State, string> = {
    rest: 'bg-surface-base border-base shadow-secondary-button',
    hover: 'hover:bg-neutral-subtle-hover hover:border-hover',
    active: 'active:bg-neutral-subtle-active active:border-hover active:shadow-button-active',
    disabled: 'disabled:bg-surface-disabled disabled:border-disabled disabled:shadow-none',
}

const tertiaryStyles: Record<State, string> = {
    rest: 'bg-neutral-rest border-base',
    hover: 'hover:bg-neutral-hover hover:border-hover',
    active: 'active:bg-neutral-active active:border-hover active:shadow-button-active',
    disabled: 'disabled:bg-surface-disabled disabled:border-disabled',
}

const ghostStyles: Record<State, string> = {
    rest: 'bg-transparent border-transparent',
    hover: 'hover:!bg-neutral-subtle-hover active:shadow-button-active',
    active: 'active:bg-neutral-subtle-active',
    disabled: 'hover:bg-transparent active:shadow-none',
}

const magicStyles: Record<State, string> = {
    rest: 'bg-magic-strong-rest border-transparent',
    hover: 'hover:bg-magic-strong-hover',
    active: 'active:bg-magic-strong-hover active:shadow-button-active',
    disabled: 'disabled:bg-magic-strong-disabled',
}

const criticalStyles: Record<State, string> = {
    rest: 'bg-critical-strong-rest border-transparent',
    hover: 'hover:bg-critical-strong-hover',
    active: 'active:bg-critical-strong-active active:shadow-button-active',
    disabled: 'disabled:bg-critical-subtle-rest',
}

const criticalSecondaryStyles: Record<State, string> = {
    rest: 'bg-surface-base border-base shadow-secondary-button',
    hover: 'hover:bg-critical-subtle-rest hover:border-hover',
    active: 'active:bg-critical-subtle-active active:border-hover active:shadow-button-active',
    disabled: 'disabled:bg-critical-subtle-rest disabled:border-disabled disabled:shadow-none',
}

const variantStyles: Record<Variant, string> = {
    primary: `${primaryStyles.rest} ${primaryStyles.hover} ${primaryStyles.active} ${primaryStyles.disabled}`,
    secondary: `${secondaryStyles.rest} ${secondaryStyles.hover} ${secondaryStyles.active} ${secondaryStyles.disabled}`,
    tertiary: `${tertiaryStyles.rest} ${tertiaryStyles.hover} ${tertiaryStyles.active} ${tertiaryStyles.disabled}`,
    ghost: `${ghostStyles.rest} ${ghostStyles.hover} ${ghostStyles.active} ${ghostStyles.disabled}`,
    magic: `${magicStyles.rest} ${magicStyles.hover} ${magicStyles.active} ${magicStyles.disabled}`,
    critical: `${criticalStyles.rest} ${criticalStyles.hover} ${criticalStyles.active} ${criticalStyles.disabled}`,
    'critical-secondary': `${criticalSecondaryStyles.rest} ${criticalSecondaryStyles.hover} ${criticalSecondaryStyles.active} ${criticalSecondaryStyles.disabled}`,
}

const commonIconStyles = '[&>svg]:text-default group-hover:[&>svg]:text-default group-active:[&>svg]:text-default group-disabled:[&>svg]:text-disabled'

const iconStyles: Record<Variant, string> = {
    primary: '[&>svg]:text-inverted group-disabled:[&>svg]:text-disabled',
    secondary: commonIconStyles,
    tertiary: commonIconStyles,
    ghost: commonIconStyles,
    critical: '[&>svg]:text-inverted group-disabled:[&>svg]:text-critical-disabled',
    magic: '[&>svg]:text-inverted group-disabled:[&>svg]:text-disabled',
    'critical-secondary': '[&>svg]:text-critical-emphasis group-disabled:[&>svg]:text-critical-disabled',
}

const commonFontStyles = 'hover:text-default active:text-default group-disabled:text-disabled'

const fontStyles: Record<Variant, string> = {
    primary: 'text-inverted group-disabled:text-disabled',
    secondary: `text-default ${commonFontStyles}`,
    tertiary: `text-default ${commonFontStyles}`,
    ghost: `text-default ${commonFontStyles}`,
    magic: 'text-inverted group-disabled:text-disabled',
    critical: 'text-inverted group-disabled:text-critical-disabled',
    'critical-secondary': 'text-critical group-disabled:text-critical-disabled',
}

const getSizeStyles = (size: SizeOptions, hasLabel: boolean, isRounded: boolean) => {
    const sizeStyles: Record<SizeOptions, string> = {
        small: `${hasLabel ? 'h-5 px-[6px] w-fit' : 'size-5'} ${isRounded ? 'rounded' : ''}`,
        medium: `${hasLabel ? 'h-7 px-2 w-fit' : 'size-7'} ${isRounded ? 'rounded-md' : ''}`,
        large: `${hasLabel ? 'h-9 px-2 w-fit' : 'size-9'} ${isRounded ? 'rounded-lg' : ''}`,
    }
    return sizeStyles[size]
}

const getLoadingStyles = (variant: Variant) => {
    const textStyles = ['critical', 'critical-secondary'].includes(variant)
        ? 'group-disabled:[&>svg]:text-critical-disabled'
        : 'group-disabled:[&>svg]:text-disabled'
    const fillStyles = ['primary', 'magic', 'critical', 'critical-secondary'].includes(variant)
        ? 'group-disabled:[&>svg]:fill-white'
        : 'group-disabled:[&>svg]:fill-gray-200'
    return `${textStyles} ${fillStyles}`
}

const disabledLinkStyles = 'pointer-events-none opacity-50'

/**
 * Basic interactive element used to trigger an action on a page.
 * When `to` prop is provided, renders as a react-router Link.
 */
export const AstraButton = (props: AstraButtonProps) => {
    const {
        variant = 'primary',
        size = 'medium',
        label,
        icon,
        'aria-label': ariaLabel,
        disabled,
        isLoading,
        fullWidth,
        endIcon,
        tightPadding,
        truncate = false,
        className,
        isRounded = true,
        ...restProps
    } = props

    const mergedClassName = astraTwMerge(
        baseStyles,
        variantStyles[variant],
        getSizeStyles(size, Boolean(label), isRounded),
        tightPadding ? 'px-1' : '',
        fullWidth ? 'w-full' : '',
        fullWidth && (endIcon || icon) ? 'justify-between' : '',
        icon && !label ? 'shrink-0' : '',
        !truncate ? 'min-w-max' : '',
        className
    )

    const content = (
        <>
            {isLoading && (
                <span className={getLoadingStyles(variant)}>
                    <Spinner size={size === 'small' ? 'xxs' : 'xs'} />
                </span>
            )}
            {!isLoading && icon && (
                <span className={`${iconStyles[variant]} ${size === 'small' ? '[&>svg]:size-3' : '[&>svg]:size-4'} stroke-[1.5]`}>{icon}</span>
            )}
            {label && (
                <AstraParagraph
                    className={`${fontStyles[variant]} ${truncate ? 'truncate' : ''}`}
                    variant={size === 'small' ? 'body-small' : 'body'}
                    weight="medium"
                    as="span"
                    data-test="input-label"
                >
                    {label}
                </AstraParagraph>
            )}
            {endIcon && <span className={`${iconStyles[variant]} ${size === 'small' ? '[&>svg]:size-3' : '[&>svg]:size-4'} stroke-[1.5]`}>{endIcon}</span>}
        </>
    )

    if ('to' in props && props.to !== undefined) {
        const { to, ref, ...linkProps } = restProps as Omit<AstraLinkButtonProps, keyof CommonProps | 'disabled'>
        return (
            <Link
                to={to}
                className={astraTwMerge(mergedClassName, disabled || isLoading ? disabledLinkStyles : '')}
                aria-label={label ?? ariaLabel}
                aria-disabled={disabled || isLoading}
                ref={ref}
                {...linkProps}
            >
                {content}
            </Link>
        )
    }

    const { ref, ...buttonProps } = restProps as Omit<AstraButtonOnlyProps, keyof CommonProps>
    return (
        <button className={mergedClassName} aria-label={label ?? ariaLabel} disabled={disabled || isLoading} ref={ref} name={label} {...buttonProps}>
            {content}
        </button>
    )
}
